import type { Candle, CandlesResponse, Instrument, Timeframe } from "@trader/shared/MarketData"
import { TIMEFRAME_SECONDS } from "@trader/shared/MarketData"
import type { CandlestickData, HistogramData, IChartApi, ISeriesApi, LogicalRange, UTCTimestamp } from "lightweight-charts"
import { call } from "../../api.ts"
import { DOWN_VOLUME, UP_VOLUME } from "./chartTheme.ts"
import { timeFormatters } from "./chartTime.ts"
import { HISTORY_WINDOW_SECONDS, TAIL_REFRESH_MS } from "./chartWindows.ts"

export type FeedStatus =
  | { readonly kind: "loading" }
  | { readonly kind: "ready" }
  | { readonly kind: "empty" }
  | { readonly kind: "failed"; readonly error: unknown }

/** Where the loaded bars came from, shown in the pane header. */
export interface FeedProvenance {
  readonly sources: ReadonlyArray<string>
  /** False when some closed bars in the loaded range are missing. */
  readonly complete: boolean
  /** Set when the chart shows stored history that ends before today (open time of the last bar). */
  readonly endsAt?: number | undefined
}

export interface ChartSeries {
  readonly chart: IChartApi
  readonly candles: ISeriesApi<"Candlestick">
  readonly volume: ISeriesApi<"Histogram">
}

/** Start loading older bars when fewer than this many are left of the view. */
const LOAD_MORE_THRESHOLD = 30

/** Newly closed bars beyond this many are drawn with one setData instead of one update each. */
const BULK_REVEAL = 50

const nowSeconds = () => Math.floor(Date.now() / 1000)

/**
 * Feeds one chart with one instrument at one timeframe: the initial window,
 * older history as the user scrolls left, and a periodic refresh of the
 * newest bars. Plain TypeScript, no React; ChartPane owns its lifetime.
 *
 * With an `end` (a replay's virtual time, set by ReplayCursor through `setEnd`)
 * it shows only bars that closed by then: the window loads around it, moving the
 * end forward reveals the bars that closed meanwhile, and moving it back (or
 * clearing it) reloads.
 */
export class CandleFeed {
  private readonly series: ChartSeries
  private readonly instrument: Instrument
  private readonly timeframe: Timeframe
  private readonly onStatus: (status: FeedStatus) => void
  private readonly onProvenance: (provenance: FeedProvenance) => void
  private readonly onBars: (bars: ReadonlyArray<Candle>, tailOnly: boolean, forming: boolean) => void
  private provenance: FeedProvenance = { sources: [], complete: true }
  /** In a replay, the last bar is the one forming at the replay's time (docs/design/0011); it changes each step. */
  private formingLast = false
  /** All loaded bars, oldest first, by UTC open time. */
  private bars: Array<Candle> = []
  /** Start of the oldest window requested so far. */
  private oldestRequested = 0
  private loadingOlder = false
  private reachedStart = false
  /** True when the chart shows stored history ending before today; nothing to refresh. */
  private showingHistory = false
  private disposed = false
  private readonly refreshTimer: ReturnType<typeof setInterval>
  /** Only bars closed by this time are shown (replay); null: everything. */
  private end: number | null
  /** The end the loaded bars reflect; trails `end` while newer bars load. */
  private shownEnd: number | null = null
  /** Bumped by every full reload; older requests' results are dropped. */
  private generation = 0
  private loadingInitial = false
  private advancing = false

  constructor(
    series: ChartSeries,
    instrument: Instrument,
    timeframe: Timeframe,
    callbacks: {
      onStatus: (status: FeedStatus) => void
      onProvenance: (provenance: FeedProvenance) => void
      /** Every change of the loaded bars; `tailOnly` when only the newest bars changed. */
      onBars: (bars: ReadonlyArray<Candle>, tailOnly: boolean, forming: boolean) => void
    },
    /** Start clipped at this time (see `setEnd`). */
    end: number | null = null
  ) {
    this.series = series
    this.instrument = instrument
    this.timeframe = timeframe
    this.onStatus = callbacks.onStatus
    this.onProvenance = callbacks.onProvenance
    this.onBars = callbacks.onBars
    this.end = end
    series.candles.applyOptions({
      priceFormat: { type: "price", precision: instrument.pricePrecision, minMove: instrument.tickSize }
    })
    const intraday = timeframe !== "1D"
    const { tickMarkFormatter, timeFormatter } = timeFormatters(intraday)
    series.chart.applyOptions({
      localization: { timeFormatter },
      timeScale: { timeVisible: intraday, tickMarkFormatter }
    })
    series.chart.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
    this.refreshTimer = setInterval(() => void this.refreshTail(), TAIL_REFRESH_MS)
    void this.loadInitial()
  }

  /** The loaded bars, oldest first. */
  currentBars(): ReadonlyArray<Candle> {
    return this.bars
  }

  /**
   * Shows only bars closed by `end` (null: everything). Later ends reveal the
   * bars that closed meanwhile; an earlier end, or switching clipping on or off, reloads.
   */
  setEnd(end: number | null): void {
    if (end === this.end) return
    const previous = this.end
    this.end = end
    if (this.disposed) return
    if (previous === null || end === null) void this.loadInitial()
    else void this.catchUp()
  }

  /** Loads the shown window again, e.g. after a sync stored new bars. */
  reload(): void {
    if (!this.disposed) void this.loadInitial()
  }

  dispose(): void {
    this.disposed = true
    clearInterval(this.refreshTimer)
    this.series.chart.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
  }

  /** Bars in [from, to); with `asOf` (a replay's time) only those closed by then, plus the one forming. */
  private async fetch(from: number, to: number, asOf?: number): Promise<ReadonlyArray<Candle>> {
    const response: CandlesResponse = await call((client) =>
      client.candles.get({
        query: { instrument: this.instrument.id, timeframe: this.timeframe, from, to, ...(asOf === undefined ? {} : { asOf }) }
      })
    )
    this.recordProvenance(response)
    // Bars as of a replay's time end with the forming one, if any: it is the tail of what is shown.
    if (asOf !== undefined) this.formingLast = response.forming === true
    return response.candles
  }

  private recordProvenance(response: CandlesResponse): void {
    if (this.disposed) return
    const sources = [...new Set([...this.provenance.sources, ...response.sources])].sort()
    const complete = this.provenance.complete && response.complete
    if (sources.join() === this.provenance.sources.join() && complete === this.provenance.complete) return
    this.provenance = { ...this.provenance, sources, complete }
    this.onProvenance(this.provenance)
  }

  private async loadInitial(): Promise<void> {
    const mine = ++this.generation
    this.loadingInitial = true
    this.onStatus({ kind: "loading" })
    this.bars = []
    this.reachedStart = false
    this.showingHistory = false
    this.render()
    this.provenance = { sources: [], complete: true }
    this.onProvenance(this.provenance)
    const window = HISTORY_WINDOW_SECONDS[this.timeframe]
    const end = this.end
    try {
      let candles: ReadonlyArray<Candle>
      if (end !== null) {
        // A replay: the window that ends at its virtual time, its closed bars and the one forming.
        const to = end + 1
        this.oldestRequested = to - window
        candles = await this.fetch(this.oldestRequested, to, end)
      } else {
        this.formingLast = false
        const to = nowSeconds() + 1
        this.oldestRequested = to - window
        candles = await this.fetch(this.oldestRequested, to).catch(async (error: unknown) => {
          // Nothing recent and no provider to fetch it: fall back to stored history below.
          if ((await this.latestStoredTime()) === null) throw error
          return []
        })
        if (candles.length === 0) candles = await this.loadStoredHistory(window)
      }
      if (this.disposed || mine !== this.generation) return
      this.bars = [...candles]
      this.shownEnd = end
      this.render()
      this.series.chart.timeScale().scrollToRealTime()
      this.onStatus({ kind: candles.length === 0 ? "empty" : "ready" })
    } catch (error) {
      if (!this.disposed && mine === this.generation) this.onStatus({ kind: "failed", error })
    } finally {
      if (mine === this.generation) {
        this.loadingInitial = false
        // The replay moved on while this window loaded.
        if (!this.disposed && this.end !== end) void this.catchUp()
      }
    }
  }

  /** Brings the shown bars to the current `end`: reveals newly closed bars, or reloads when the end moved back. */
  private async catchUp(): Promise<void> {
    if (this.loadingInitial || this.advancing) return
    this.advancing = true
    try {
      while (!this.disposed && this.end !== null && this.end !== this.shownEnd) {
        const end = this.end
        const mine = this.generation
        const last = this.bars.at(-1)
        if (this.shownEnd === null || end < this.shownEnd || last === undefined || end - this.shownEnd > HISTORY_WINDOW_SECONDS[this.timeframe]) {
          void this.loadInitial()
          return
        }
        // The last shown bar again (forming, or incomplete in the store), and everything after it.
        const fresh = await this.fetch(last.time, end + 1, end)
        if (this.disposed || mine !== this.generation) return
        this.reveal(fresh)
        this.shownEnd = end
      }
    } catch {
      // Transient: the next end change or the refresh timer retries.
    } finally {
      this.advancing = false
    }
  }

  /** Appends bars since the last ones shown, replacing the last when it comes again (a forming bar grows). */
  private reveal(bars: ReadonlyArray<Candle>): void {
    const changed: Array<Candle> = []
    for (const candle of bars) {
      const last = this.bars.at(-1)
      if (last !== undefined && candle.time === last.time) {
        this.bars[this.bars.length - 1] = candle
        changed.push(candle)
      } else if (last === undefined || candle.time > last.time) {
        this.bars.push(candle)
        changed.push(candle)
      }
    }
    if (changed.length === 0) return
    if (changed.length > BULK_REVEAL) {
      this.render()
      return
    }
    for (const candle of changed) {
      this.series.candles.update(this.toCandle(candle))
      this.series.volume.update(this.toVolume(candle))
    }
    this.onBars(this.bars, true, this.formingLast)
  }

  private latestStoredTime(): Promise<number | null> {
    return call((client) => client.candles.latest({ query: { instrument: this.instrument.id, timeframe: this.timeframe } }))
      .then(({ time }) => time)
      .catch(() => null)
  }

  /**
   * The recent window was empty: open on the newest stored window instead, e.g.
   * a dataset that ends months ago. The badge shows where the data ends, and the
   * tail isn't refreshed (there is no live source).
   */
  private async loadStoredHistory(window: number): Promise<ReadonlyArray<Candle>> {
    const latest = await this.latestStoredTime()
    if (latest === null || latest >= this.oldestRequested) return []
    const to = latest + TIMEFRAME_SECONDS[this.timeframe]
    this.oldestRequested = to - window
    const candles = await this.fetch(this.oldestRequested, to)
    this.showingHistory = true
    this.provenance = { ...this.provenance, endsAt: latest }
    this.onProvenance(this.provenance)
    return candles
  }

  private readonly onVisibleRangeChange = (range: LogicalRange | null): void => {
    if (range !== null && range.from < LOAD_MORE_THRESHOLD) void this.loadOlder()
  }

  private async loadOlder(): Promise<void> {
    if (this.loadingOlder || this.reachedStart || this.bars.length === 0) return
    this.loadingOlder = true
    const mine = this.generation
    const to = this.oldestRequested
    const from = to - HISTORY_WINDOW_SECONDS[this.timeframe]
    try {
      const older = await this.fetch(from, to)
      if (this.disposed || mine !== this.generation) return
      this.oldestRequested = from
      // An empty window means we've scrolled past the provider's history.
      if (older.length === 0) {
        this.reachedStart = true
        return
      }
      // Keep the view still: shift it right by the number of bars added on the left.
      const visible = this.series.chart.timeScale().getVisibleLogicalRange()
      this.bars = [...older, ...this.bars]
      this.render()
      if (visible !== null) {
        this.series.chart.timeScale().setVisibleLogicalRange({
          from: visible.from + older.length,
          to: visible.to + older.length
        })
      }
    } catch {
      // Leave the chart as it is; the next scroll retries.
    } finally {
      this.loadingOlder = false
    }
  }

  private async refreshTail(): Promise<void> {
    if (this.end !== null) {
      // A replay shows no live tail; retry revealing bars an earlier attempt missed.
      void this.catchUp()
      return
    }
    const last = this.bars.at(-1)
    if (last === undefined || document.hidden || this.showingHistory || this.loadingInitial) return
    const mine = this.generation
    try {
      const fresh = await this.fetch(last.time, nowSeconds() + 1)
      if (this.disposed || mine !== this.generation) return
      let changed = false
      for (const candle of fresh) {
        if (candle.time === this.bars.at(-1)?.time) this.bars[this.bars.length - 1] = candle
        else if (candle.time > (this.bars.at(-1)?.time ?? 0)) this.bars.push(candle)
        else continue
        changed = true
        this.series.candles.update(this.toCandle(candle))
        this.series.volume.update(this.toVolume(candle))
      }
      if (changed) this.onBars(this.bars, true, this.formingLast)
    } catch {
      // Transient; the next tick retries.
    }
  }

  private render(): void {
    this.series.candles.setData(this.bars.map((candle) => this.toCandle(candle)))
    this.series.volume.setData(this.bars.map((candle) => this.toVolume(candle)))
    this.onBars(this.bars, false, this.formingLast)
  }

  private toCandle(candle: Candle): CandlestickData<UTCTimestamp> {
    const time = candle.time as UTCTimestamp
    return { time, open: candle.open, high: candle.high, low: candle.low, close: candle.close }
  }

  private toVolume(candle: Candle): HistogramData<UTCTimestamp> {
    const color = candle.close >= candle.open ? UP_VOLUME : DOWN_VOLUME
    return { time: candle.time as UTCTimestamp, value: candle.volume, color }
  }
}
